import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Megaphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { faqs } from "./data";
import PricingSection from "./PricingSection";
import { cn } from "@/lib/utils";

const STEPS = [
  {
    n: "01",
    title: "tell qwip about your team",
    body: "One sentence. Size, cities, budget, the kind of day you want.",
  },
  {
    n: "02",
    title: "get three complete days",
    body: "Not a venue list. Costed itineraries with travel, food and an evening.",
  },
  {
    n: "03",
    title: "tweak it, then hand it over",
    body: "Ask for changes in plain words. qwip can source and book the winner.",
  },
];

export default function Closing({ onPlan }: { onPlan: () => void }) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const navigate = useNavigate();

  return (
    <>
      <section id="how-it-works" className="border-t border-border py-24 scroll-mt-20">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
              what this is
            </p>
            <h2 className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-5xl text-foreground mb-4">
              offsite planning, done in{" "}
              <span className="font-cormorant italic font-normal">minutes.</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Distributed teams lose weeks to spreadsheets, group chats and venue emails. qwip does
              the boring part and leaves you the decision.
            </p>
          </div>

          <div className="grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-3 desktop:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl border border-border bg-card p-8"
              >
                <p className="font-funnel text-sm text-muted-foreground mb-5">{s.n}</p>
                <h3 className="font-funnel font-bold text-xl text-foreground mb-3">{s.title}</h3>
                <p className="text-muted-foreground">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PricingSection onCta={() => onPlan()} />

      <section className="border-t border-border py-24">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
          <div className="grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-[320px_1fr] desktop:grid-cols-[360px_1fr] gap-12">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
                questions
              </p>
              <h2 className="font-funnel font-bold text-3xl laptop:text-4xl desktop:text-4xl text-foreground">
                the honest{" "}
                <span className="font-cormorant italic font-normal">answers.</span>
              </h2>
            </div>

            <div className="divide-y divide-border border-y border-border">
              {faqs.slice(0, 4).map((f, i) => (
                <div key={f.q}>
                  <button
                    onClick={() => setOpenFaq((o) => (o === i ? null : i))}
                    className="w-full flex items-center justify-between gap-6 py-5 text-left"
                  >
                    <span className="font-funnel font-bold text-foreground">{f.q}</span>
                    <ChevronDown
                      size={17}
                      className={cn(
                        "text-muted-foreground shrink-0 transition-transform",
                        openFaq === i && "rotate-180"
                      )}
                    />
                  </button>
                  {openFaq === i && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="pb-6 text-muted-foreground max-w-2xl"
                    >
                      {f.a}
                    </motion.p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border py-24">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
          <div className="rounded-2xl bg-primary text-primary-foreground px-8 tablet:px-12 laptop:px-14 desktop:px-14 py-16 text-center">
            <h2 className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-5xl mb-5">
              one sentence in.{" "}
              <span className="font-cormorant italic font-normal">a whole day out.</span>
            </h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto mb-9">
              This is an internal concept demo. Plans, prices and availability are illustrative.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={onPlan}
                className="rounded-full bg-background text-foreground px-7 py-3.5 text-base hover:bg-background/90 transition-colors"
              >
                start a brief
              </button>
              <button
                onClick={() => navigate("/offsite-demo/ads")}
                className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 px-7 py-3.5 text-base hover:border-primary-foreground/60 transition-colors"
              >
                <Megaphone size={15} /> ad concepts
              </button>
            </div>
          </div>
          <p className="mt-10 text-center text-sm text-muted-foreground">
            qwip offsites · internal concept · not a live product
          </p>
        </div>
      </section>
    </>
  );
}
